<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useBiStore } from '~/bi/store/bi.store';

const bi_store = useBiStore();
const iframe_ref = ref(null);

const html_url = computed(() => bi_store.selected_dashboard_details?.widgets?.content?.html_url || '');

const state = reactive({
  html_content: '',
  is_loading: false,
});

// Let reports print directly within the sandbox instead of copying HTML into the parent document.
/*
function handleIframeMessage(event) {
  // A srcdoc iframe without `allow-same-origin` has an opaque origin serialized as "null".
  // The source check prevents unrelated windows from invoking this privileged action.
  if (
    event.origin === 'null' && typeof event.data === 'object'
    && event.source === iframe_ref.value?.contentWindow
    && event.data !== null
  ) {
    if (event.data.type === 'print-html') {
      const { content, baseUrl } = event.data;

      const tempIframe = document.createElement('iframe');
      tempIframe.style.position = 'fixed';
      tempIframe.style.width = '0';
      tempIframe.style.height = '0';
      tempIframe.style.border = 'none';

      document.body.appendChild(tempIframe);

      const tempDoc = tempIframe.contentDocument || tempIframe.contentWindow.document;
      tempDoc.open();

      // Inject the <base> tag right at the start so stylesheets resolve properly
      tempDoc.write(`
        <base href="${baseUrl}">
        ${content}
      `);
      tempDoc.close();

      // Give stylesheets a split second to parse before launching print
      tempIframe.contentWindow.focus();
      tempIframe.contentWindow.print();

      document.body.removeChild(tempIframe);
    }

    if (event.data.type === 'bi_iframe_link_click'
      && typeof event.data.url === 'string') {
      try {
        const urlToOpen = new URL(event.data.url);

        // SECURITY: Strictly enforce HTTP/HTTPS to prevent XSS via javascript: or data: URIs
        if (['http:', 'https:'].includes(urlToOpen.protocol)) {
          window.open(urlToOpen.href, '_blank', 'noopener,noreferrer');
        }
        else {
          console.warn('Blocked opening non-HTTP link from BI iframe:', event.data.url);
        }
      }
      catch (e) {
        console.error('Invalid URL received from BI iframe:', event.data.url, e.toString());
      }
    }
  }
}

onMounted(() => {
  window.addEventListener('message', handleIframeMessage);
});

onUnmounted(() => {
  window.removeEventListener('message', handleIframeMessage);
});
*/

watch(html_url, async (url) => {
  if (!url) {
    state.html_content = '';
    return;
  }
  try {
    state.is_loading = true;
    const response = await fetch(url);
    let html = await response.text();

    // NOTE: The following patches are applied to the HTML content to fix issues with the dashboard rendering in an iframe. The first patch fixes a bug in the CanvasRenderingContext2D.arc method that causes negative radius values to be treated as 0, which can lead to rendering issues. The second patch triggers a resize event after the page has loaded to ensure that the dashboard is rendered correctly. These were added to fix some issues with real client dashboards that were not rendering correctly in the iframe.
    const arc_patch = `<script>(function(){var _a=CanvasRenderingContext2D.prototype.arc;CanvasRenderingContext2D.prototype.arc=function(x,y,r,sA,eA,ccw){_a.call(this,x,y,r<0?0:r,sA,eA,ccw);};})();<\/script>`;
    const resize_script = `<script>window.addEventListener('load',function(){setTimeout(function(){window.dispatchEvent(new Event('resize'));},100);});<\/script>`;

    // Disabled alongside the parent message handler so native report links still work.
    /*
    const link_interceptor_script = `<script>
      document.addEventListener('click', function(e) {
        var a = e.target.closest('a');
        if (a && a.getAttribute('href')) {
          e.preventDefault(); // Stop iframe from trying to navigate
          window.parent.postMessage({ type: 'bi_iframe_link_click', url: a.href }, '*');
        }
      });
    <\/script>`;
    */

    html = html.includes('<head>')
      ? html.replace('<head>', `<head>${arc_patch}`)
      : arc_patch + html;

    html = html.includes('</body>')
      ? html.replace('</body>', `${resize_script}</body>`)
      : html + resize_script;

    state.html_content = html;
  }
  catch {
    state.html_content = '';
  }
  finally {
    state.is_loading = false;
  }
}, { immediate: true });
</script>

<template>
  <!-- RIL wanted BI to have dashboards that accepted HTML so they could create their own custom dashboards and see them. However, HTML files are not usually accepted for security purposes. But we won't be able to loosen up the CSP policy as it would open up the floodgates for everything. -->
  <!-- Therefore, if issues are reported in RIL environment, check if they work fine on local/dev. If they do, it is an issue with security. Find an alternative, either by making the HTML safer or checking the CSP -->
  <HawkLoader v-if="state.is_loading" container_class="bg-gray-50 min-h-[calc(100vh-167px)]" />
  <iframe
    v-else
    ref="iframe_ref"
    class="w-full border-0 block min-h-[calc(100vh-167px)]"
    sandbox="allow-scripts allow-modals allow-downloads allow-popups"
    referrerpolicy="no-referrer"
    allow=""
    :srcdoc="state.html_content"
  />
</template>
